<template>
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    bg-color="white"
    class="relative"
    :main-layout-scroll-class="scrollClass"
    :use-stable-scroll-gutter="['logs', 'credits'].includes(selectedTab)"
  >
    <template v-if="getHasApiAccess" #toolbar>
      <div class="flex flex-col w-full">
        <!-- Top Header -->
        <div class="flex items-center gap-3 p-3 border-b border-border-normal">
          <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-900">
            <DataApiIcon class="text-white" />
          </div>
          <BaseText type="label" size="sm" class="text-text-muted">
            API
          </BaseText>
          <transition>
            <BaseText v-if="getApiVersion" type="label" size="xs" class="text-text-muted px-1.5 py-1 rounded-lg bg-neutral-25">
              v{{ getApiVersion }}
            </BaseText>
          </transition>
        </div>
        <!-- Tab Switcher & Docs Button -->
        <div class="relative flex gap-2 w-full px-3 py-2 border-border-normal"
        :class="{'border-b': selectedTab !== 'logs'}">
          <router-link :to="{ name: 'ApiView' }" class="api-view-tab"
          :class="{'active': selectedTab === 'overview'}" style="width: 82px">
            Overview
            <transition name="tab">
              <div v-if="selectedTab === 'overview'" class="api-tab-underline" />
            </transition>
          </router-link>
          <router-link :to="{ name: 'ApiLogsView' }" class="api-view-tab"
          :class="{'active': selectedTab === 'logs'}" style="width: 52px">
            Logs
            <transition name="tab">
              <div v-if="selectedTab === 'logs'" class="api-tab-underline" />
            </transition>
          </router-link>
          <router-link :to="{ name: 'ApiCreditsView' }" class="api-view-tab"
          :class="{'active': selectedTab === 'credits'}" style="width: 126px">
            Pricing / Credits
            <transition name="tab">
              <div v-if="selectedTab === 'credits'" class="api-tab-underline" />
            </transition>
          </router-link>
          <!-- View Docs Button -->
          <a class="ml-auto group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          href="https://docs.foreplay.co/" target="_blank">
            <ViewDocsIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              View Docs
            </BaseText>
          </a>
          <!-- border extension -->
          <div class="border-extension border-t border-b border-border-normal" />
        </div>
      </div>
    </template>
    <template v-if="selectedTab === 'logs'" #filter>
      <div id="logs-filter-bar" ref="filterBar" class="border border-border-normal bg-white"
      style="border-bottom-right-radius: 8px;">
        <portal-target name="api-filter-toolbar" />
      </div>
    </template>
    <template #content>
      <ApiPaywallState
        v-if="!getHasApiAccess"
        :subscription-type="apiSubscription"
      />
      <ApiOverview 
        v-else-if="selectedTab === 'overview'"
        @navigate-to-credits="routeToView('ApiCreditsView')"
      />
      <ApiLogs
        v-else-if="selectedTab === 'logs'"
        :disable-scroll.sync="disableScroll"
      />
      <ApiCredits
        v-else-if="selectedTab === 'credits'"
      />
    </template>
  </MainLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'
import FirebaseAPI from '@/api/firebase'

import MainLayout from '../../layouts/MainLayout.vue'
import ApiPaywallState from '../../components/api/ApiPaywallState.vue'

// Main Views
import ApiOverview from '../../components/api/ApiOverview.vue'
import ApiLogs from '../../components/api/ApiLogs.vue'
import ApiCredits from '../../components/api/ApiCredits.vue'

// Icons
import DataApiIcon from '../../components/globals/Icons/SidebarIcons/DataApiIcon.vue'
import ViewDocsIcon from '../../components/globals/Icons/ViewDocsIcon.vue'

export default {
  name: 'DataApiView',
  components: {
    MainLayout,
    ApiOverview,
    ApiLogs,
    ApiCredits,
    DataApiIcon,
    ViewDocsIcon,
    ApiPaywallState
  },
  data () {
    return {
      selectedTab: '', // Intitialized in mounted,
      disableScroll: false,
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserSubscription', 'getTeamSubscription', 'getIsLegacy', 'getHasApiAccess', 'getBasePlanInterval']),
    ...mapGetters('MiscModule', ['getApiVersion']),
    apiSubscription () {
      const isLegacy = this.getIsLegacy
      return {
        isLegacy,
        intervalType: this.getBasePlanInterval
      }
    },
    scrollClass () {
      let scrollClass = this.disableScroll ? 'overflow-y-hidden' : 'overflow-y-auto'
      scrollClass += ' overflow-x-hidden'
      return scrollClass
    }
  },
  async mounted () {
    if (this.$route.name === 'ApiLogsView') {
      this.selectedTab = 'logs'
    } else if (this.$route.name === 'ApiCreditsView') {
      this.selectedTab = 'credits'
    } else {
      this.selectedTab = 'overview'
    }

    // Set initial top bar styles
    this.$nextTick(() => {
      if (this.$refs.filterBar) {
        this.$refs.filterBar.style.position = 'sticky'
        this.$refs.filterBar.style.top = '16px'
        this.$refs.filterBar.style.zIndex = '40000'
      }
    })

    this.fetchApiInfo()
  },
  methods: {
    ...mapActions('MiscModule', ['fetchApiVersion']),
    async fetchApiInfo () {
      if (!this.getApiVersion) this.fetchApiVersion()
    },
    routeToView (view) {
      this.$router.push({ name: view })
    },
    onScroll (e, position) {
      const marginValue =
        position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding =
        position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5
      if (this.$refs.filterBar) {
        this.$refs.filterBar.style.marginLeft = `${marginValue}px`
        this.$refs.filterBar.style.marginRight = `${marginValue}px`
        this.$refs.filterBar.style.marginTop = `${marginValue}px`
        this.$refs.filterBar.style.borderTopLeftRadius = `${rounding}px`
        this.$refs.filterBar.style.borderBottomLeftRadius = `${rounding}px`
        this.$refs.filterBar.style.borderTopRightRadius = `${rounding}px`
        this.$refs.filterBar.style.borderBottomRightRadius = `${Math.max(rounding, 8)}px`
      }
      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue
          ? position.scrollTop / 15
          : maxValue
      }

      if (position.scrollTop && this.$refs.filterBar) {
        if (position.scrollTop > 0) this.$refs.filterBar.classList.add('toolbar-container')
        this.$refs.filterBar.style.setProperty('border-top-left-radius', `${shadowSection(14)}px`, 'important')
        this.$refs.filterBar.style.setProperty('border-top-right-radius', `${shadowSection(14)}px`, 'important')
        this.$refs.filterBar.style.setProperty('border-bottom-left-radius', `${shadowSection(14)}px`, 'important')
        this.$refs.filterBar.style.setProperty('border-bottom-right-radius', `${Math.max(shadowSection(14), 8)}px`, 'important')
      } else {
        if (this.$refs.filterBar) {
          this.$refs.filterBar.classList.remove('toolbar-container')
        }
      }
    }
  }
}
</script>

<style scoped>
.api-view-tab {
  position: relative;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.api-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.api-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: transparent;
  font-weight: 500;
}
.api-tab-underline {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 0px;
  z-index: 40001;
  border-bottom: 1px solid #06070F; /* border-icon-loud */
}
.border-extension {
  position: absolute;
  top: -1px;
  left: 100%;
  height: calc(100% + 2px);
  width: 16px;
}

/* Vue <transition> classes */
.tab-enter-active, .tab-leave-active {
  transition: width 150ms ease-in-out;
}
.tab-enter-from, .tab-enter, .tab-leave-to {
  width: 0;
}
.tab-enter-to, .tab-leave-from {
  width: 100%;
}
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
